import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useUpdateStatus } from '@/hooks/use-eirsaliye';
import type { EirsaliyeDetail } from '@/lib/eirsaliye-service';
import {
  FileText, Truck, MapPin, Package, Mail, Eye, EyeOff,
  CheckCircle, AlertTriangle, XCircle, RotateCcw,
} from 'lucide-react';

interface EirsaliyeDetailViewProps {
  data: EirsaliyeDetail;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  const parts = dateStr.split('-');
  if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
  return dateStr;
}

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return `₺${value.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function InfoRow({ label, value }: { label: string; value: string | number | null }) {
  return (
    <div className="flex justify-between py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right max-w-[60%] break-words">{value ?? '-'}</span>
    </div>
  );
}

export function EirsaliyeDetailView({ data }: EirsaliyeDetailViewProps) {
  const [showRawText, setShowRawText] = useState(false);
  const updateStatusMutation = useUpdateStatus();

  const handleStatusChange = (status: string) => {
    updateStatusMutation.mutate({ id: data.id, status });
  };

  const items = data.eirsaliye_items || [];
  const allItemsStockImported =
    items.length > 0 && items.every(item => item.stock_imported);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6" />
            {data.irsaliye_no}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            ETTN: {data.ettn}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {allItemsStockImported && data.status === 'active' && (
            <Badge
              variant="secondary"
              className="bg-emerald-600 text-white hover:bg-emerald-600 border-transparent"
            >
              Kapatildi
            </Badge>
          )}
          {data.status === 'active' && !allItemsStockImported && (
            <Badge variant="default">Aktif</Badge>
          )}
          {data.status === 'needs_review' && (
            <Badge variant="destructive">Inceleme Gerekli</Badge>
          )}
          {data.status === 'corrected' && (
            <Badge variant="secondary">Duzeltildi</Badge>
          )}
          {data.status === 'ignored' && (
            <Badge variant="outline">Yok Sayildi</Badge>
          )}
          {data.parsed_by === 'ai_fallback' && (
            <Badge variant="secondary">AI Parse</Badge>
          )}
          {data.parse_confidence !== null && (
            <Badge variant="outline" className="text-xs">
              Guven: %{Math.round(data.parse_confidence * 100)}
            </Badge>
          )}
        </div>
      </div>

      {/* Status actions */}
      {data.status === 'needs_review' && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800">
          <AlertTriangle className="w-5 h-5 text-orange-600" />
          <span className="text-sm text-orange-700 dark:text-orange-300 flex-1">
            Bu irsaliye inceleme bekliyor.
          </span>
          <Button size="sm" variant="outline" onClick={() => handleStatusChange('active')}>
            <CheckCircle className="w-3 h-3 mr-1" /> Onayla
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleStatusChange('ignored')}>
            <XCircle className="w-3 h-3 mr-1" /> Yok Say
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Genel Bilgiler */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Genel Bilgiler</CardTitle>
          </CardHeader>
          <CardContent className="space-y-0">
            <InfoRow label="Irsaliye No" value={data.irsaliye_no} />
            <InfoRow label="ERP Irsaliye No" value={data.erp_irsaliye_no} />
            <InfoRow label="Irsaliye Tarihi" value={formatDate(data.irsaliye_tarihi)} />
            <InfoRow label="Irsaliye Zamani" value={data.irsaliye_zamani} />
            <InfoRow label="Sevk Tarihi" value={formatDate(data.sevk_tarihi)} />
            <InfoRow label="Sevk Zamani" value={data.sevk_zamani} />
            <InfoRow label="Senaryo" value={data.senaryo} />
            <InfoRow label="Irsaliye Tipi" value={data.irsaliye_tipi} />
            <InfoRow label="Bayi Kodu" value={data.bayi_kodu} />
            <InfoRow label="Toplam Adet" value={data.toplam_adet} />
            <InfoRow label="Irsaliye Maliyeti (Mars)" value={formatCurrency(data.estimated_total_cost)} />
            <InfoRow label="Sube" value={data.sube} />
          </CardContent>
        </Card>

        {/* Tasiyici Bilgileri */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1">
              <Truck className="w-4 h-4" /> Tasiyici Bilgileri
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-0">
            <InfoRow label="Tasiyici Firma" value={data.tasiyici_firma_unvan} />
            <InfoRow label="Tasiyici VKN" value={data.tasiyici_firma_vkn} />
            <InfoRow label="Arac Plaka" value={data.arac_plaka} />
            <InfoRow label="Dorse Plaka" value={data.dorse_plaka} />
            <InfoRow label="Sofor" value={data.sofor_ad} />
            <InfoRow label="Sofor TCKN" value={data.sofor_tckn} />
            <InfoRow label="Sofor Telefon" value={data.sofor_telefon} />
            <Separator className="my-2" />
            <InfoRow label="Tasima No" value={data.tasima_no} />
            <InfoRow label="Siparis/Teslimat No" value={data.siparis_teslimat_no} />
            <InfoRow label="Musteri Siparis No" value={data.musteri_siparis_no} />
          </CardContent>
        </Card>
      </div>

      {/* Adresler */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1">
              <MapPin className="w-4 h-4" /> Teslim Adresi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{data.teslim_adres || '-'}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1">
              <MapPin className="w-4 h-4" /> Cikis Depo Adresi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{data.cikis_depo_adres || '-'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Kalemler */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-1">
            <Package className="w-4 h-4" /> Kalemler ({data.eirsaliye_items?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!data.eirsaliye_items?.length ? (
            <p className="text-sm text-muted-foreground">Kalem bulunamadi.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground text-xs">
                  <th className="text-left pb-2 pr-3">Sira</th>
                  <th className="text-left pb-2 pr-3">Malzeme No</th>
                  <th className="text-left pb-2 pr-3">Malzeme Tanimi</th>
                  <th className="text-right pb-2 pr-3">Miktar</th>
                  <th className="text-left pb-2 pr-3">Birim</th>
                  <th className="text-left pb-2">Durum</th>
                </tr>
              </thead>
              <tbody>
                {data.eirsaliye_items
                  .sort((a, b) => a.sira_no - b.sira_no)
                  .map((item) => (
                    <tr key={item.id} className="border-b last:border-0">
                      <td className="py-1.5 pr-3">{item.sira_no}</td>
                      <td className="py-1.5 pr-3 font-mono text-xs">{item.malzeme_no || '-'}</td>
                      <td className="py-1.5 pr-3">{item.malzeme_tanimi}</td>
                      <td className="py-1.5 pr-3 text-right font-medium">{item.miktar}</td>
                      <td className="py-1.5 pr-3">{item.olcu_birimi}</td>
                      <td className="py-1.5">
                        {item.stock_imported ? (
                          item.received_mode === 'manual_received' ? (
                            <Badge variant="secondary" className="text-[10px]">Manuel Alindi</Badge>
                          ) : (
                            <Badge variant="default" className="text-[10px]">Stoga Alindi</Badge>
                          )
                        ) : (
                          <Badge variant="outline" className="text-[10px]">Bekliyor</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Email metadata */}
      {data.eirsaliye_emails && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1">
              <Mail className="w-4 h-4" /> Email Bilgileri
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-0">
            <InfoRow label="Gonderen" value={data.eirsaliye_emails.from_address} />
            <InfoRow label="Konu" value={data.eirsaliye_emails.subject} />
            <InfoRow label="Alindigi Tarih" value={data.eirsaliye_emails.received_at ? new Date(data.eirsaliye_emails.received_at).toLocaleString('tr-TR') : null} />
            {data.eirsaliye_attachments && (
              <>
                <InfoRow label="Dosya Adi" value={data.eirsaliye_attachments.file_name} />
                <InfoRow label="Dosya Boyutu" value={data.eirsaliye_attachments.file_size_bytes ? `${(data.eirsaliye_attachments.file_size_bytes / 1024).toFixed(1)} KB` : null} />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Raw text (debug) */}
      {data.raw_text && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Ham Metin (Debug)</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowRawText(!showRawText)}
              >
                {showRawText ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                {showRawText ? 'Gizle' : 'Goster'}
              </Button>
            </div>
          </CardHeader>
          {showRawText && (
            <CardContent>
              <ScrollArea className="h-64 w-full rounded border bg-muted/50 p-4 font-mono text-xs whitespace-pre-wrap">
                {data.raw_text}
              </ScrollArea>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}
